/**
 * Brazilian Investor Demo Page
 * Main showcase page for Brazilian market investor presentation
 *
 * This page demonstrates:
 * 1. AI-powered invoice extraction
 * 2. NF-e integration
 * 3. Brazilian tax handling
 * 4. Competitive positioning vs. Conta Azul
 */
import React, { useState } from 'react';
import {
  Sparkles,
  FileText,
  Calculator,
  BarChart3,
  Globe,
  Shield,
  ChevronRight,
  Target,
  TrendingUp,
  Users,
  Building2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  AIExtractionDemo,
  CompetitiveComparison,
  TaxWidget,
  NfeDetailPanel,
} from '../components/demo';

type DemoSection = 'ai' | 'nfe' | 'comparison' | 'market';

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
  trend?: 'up' | 'down';
  className?: string;
}

function MetricCard({ icon, label, value, subtext, trend, className }: MetricCardProps) {
  return (
    <div className={cn('p-6 rounded-xl bg-white border border-gray-200 shadow-sm', className)}>
      <div className="flex items-start justify-between">
        <div className="p-2 rounded-lg bg-teal-50">{icon}</div>
        {trend && (
          <span
            className={cn(
              'text-xs font-medium px-2 py-0.5 rounded-full',
              trend === 'up' ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'
            )}
          >
            {trend === 'up' ? '+12%' : '-8%'}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
    </div>
  );
}

export default function BrazilianDemoPage() {
  const [activeSection, setActiveSection] = useState<DemoSection>('ai');

  const sections = [
    { id: 'ai' as const, label: 'Demo IA', icon: Sparkles },
    { id: 'nfe' as const, label: 'NF-e', icon: FileText },
    { id: 'comparison' as const, label: 'Comparativo', icon: BarChart3 },
    { id: 'market' as const, label: 'Mercado', icon: Target },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2 text-teal-200 mb-4">
            <Globe className="h-5 w-5" />
            <span className="text-sm font-medium">Mercado Brasileiro</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">
            Finova para o Brasil
          </h1>
          <p className="mt-4 text-xl text-teal-100 max-w-2xl">
            Sistema financeiro AI-native para PMEs brasileiras.
            Multi-moeda, NF-e integrado, interface moderna.
          </p>

          {/* Key Stats */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">70%</p>
              <p className="text-sm text-teal-200">Automação com IA</p>
            </div>
            <div className="p-4 rounded-lg bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">R$ 15M</p>
              <p className="text-sm text-teal-200">ARR Target (3 anos)</p>
            </div>
            <div className="p-4 rounded-lg bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">175k</p>
              <p className="text-sm text-teal-200">Empresas Cross-border</p>
            </div>
            <div className="p-4 rounded-lg bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">726</p>
              <p className="text-sm text-teal-200">Testes Automatizados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeSection === section.id
                      ? 'border-teal-500 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* AI Demo Section */}
        {activeSection === 'ai' && (
          <div className="space-y-10">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900">
                Extração Inteligente com IA
              </h2>
              <p className="mt-2 text-gray-600">
                Nossa IA extrai automaticamente os campos de notas fiscais.
                O que levaria 5 minutos, leva 30 segundos.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Demo Widget */}
              <AIExtractionDemo autoPlay={false} className="lg:sticky lg:top-24" />

              {/* Benefits */}
              <div className="space-y-6">
                <div className="p-6 rounded-xl bg-white border shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Economia de Tempo Real
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-gray-400 line-through">5 min</p>
                        <p className="text-xs text-gray-400">Manual</p>
                      </div>
                      <ChevronRight className="h-6 w-6 text-gray-300" />
                      <div className="text-center">
                        <p className="text-3xl font-bold text-teal-600">30 seg</p>
                        <p className="text-xs text-teal-600">Com IA</p>
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        <strong>100 notas/mês</strong> = 7.5 horas economizadas
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Por ano</strong> = 90 horas (mais de 2 semanas de trabalho)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-white border shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Como Funciona
                  </h3>
                  <div className="space-y-3">
                    {[
                      { step: '1', text: 'Upload do PDF/imagem da nota fiscal' },
                      { step: '2', text: 'IA extrai todos os campos automaticamente' },
                      { step: '3', text: 'Revisar apenas campos com baixa confiança' },
                      { step: '4', text: 'Confirmar e lançar - sistema aprende com correções' },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-3">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-600 text-sm font-medium">
                          {item.step}
                        </span>
                        <span className="text-sm text-gray-600">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200">
                  <h3 className="font-semibold text-teal-900 mb-2">
                    Precisão que Melhora com o Tempo
                  </h3>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Inicial</span>
                        <span>3 meses</span>
                        <span>Estável</span>
                      </div>
                      <div className="h-2 bg-teal-200 rounded-full relative overflow-hidden">
                        <div className="absolute left-0 top-0 h-full w-[92%] bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full" />
                      </div>
                      <div className="flex justify-between text-sm font-medium mt-1">
                        <span className="text-teal-600">92%</span>
                        <span className="text-teal-600">96%</span>
                        <span className="text-emerald-600">98%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NF-e Section */}
        {activeSection === 'nfe' && (
          <div className="space-y-10">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900">
                Integração NF-e Completa
              </h2>
              <p className="mt-2 text-gray-600">
                Toda a compliance fiscal brasileira integrada nativamente.
                NF-e, SPED, cálculo de impostos automático.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* NF-e Detail Example */}
              <NfeDetailPanel
                number="000001234"
                series="1"
                accessKey="35260112345678000199550010000012341234567890"
                status="autorizada"
                protocol="135260000012345"
                authDate="15/01/2026 10:30"
                className="h-fit"
              />

              {/* Tax Widget */}
              <TaxWidget
                icmsToCollect={12500}
                pisToCollect={2850}
                cofinsToCollect={13200}
                issRetained={4750}
                period="Janeiro 2026"
              />
            </div>

            {/* Compliance Features */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-white border shadow-sm">
                <Shield className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-gray-900">SPED Ready</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Exportação automática para ECD, EFD-ICMS/IPI e EFD-PIS/COFINS.
                  Um clique para gerar os arquivos.
                </p>
              </div>
              <div className="p-6 rounded-xl bg-white border shadow-sm">
                <Calculator className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-gray-900">Cálculo Automático</h3>
                <p className="text-sm text-gray-600 mt-2">
                  ICMS, PIS, COFINS, ISS, IRRF, INSS calculados automaticamente
                  com base no estado e tipo de operação.
                </p>
              </div>
              <div className="p-6 rounded-xl bg-white border shadow-sm">
                <FileText className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-gray-900">DANFE & XML</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Download do XML original e geração de DANFE para impressão.
                  Histórico completo de todas as notas.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Comparison Section */}
        {activeSection === 'comparison' && <CompetitiveComparison />}

        {/* Market Section */}
        {activeSection === 'market' && (
          <div className="space-y-10">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900">
                Oportunidade de Mercado
              </h2>
              <p className="mt-2 text-gray-600">
                O mercado brasileiro de software financeiro para PMEs é de R$ 2.5 bilhões.
                Entramos com tecnologia de 2026.
              </p>
            </div>

            {/* Market Metrics */}
            <div className="grid md:grid-cols-4 gap-4">
              <MetricCard
                icon={<Building2 className="h-5 w-5 text-teal-600" />}
                label="Empresas Endereçáveis"
                value="175 mil"
                subtext="Com operação cross-border"
              />
              <MetricCard
                icon={<Target className="h-5 w-5 text-teal-600" />}
                label="ARR Target (3 anos)"
                value="R$ 15-25M"
                subtext="5.000 clientes pagantes"
                trend="up"
              />
              <MetricCard
                icon={<Users className="h-5 w-5 text-teal-600" />}
                label="Conta Azul tem"
                value="150 mil"
                subtext="Clientes ativos"
              />
              <MetricCard
                icon={<TrendingUp className="h-5 w-5 text-teal-600" />}
                label="LTV:CAC"
                value="9:1"
                subtext="Margem bruta 80%+"
              />
            </div>

            {/* Pricing Tiers */}
            <div className="p-8 rounded-xl bg-white border shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Modelo de Precificação</h3>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { tier: 'Grátis', price: 'R$ 0', features: ['50 transações/mês', '1 usuário'] },
                  { tier: 'Starter', price: 'R$ 79/mês', features: ['500 transações', '2 usuários', 'Suporte email'] },
                  { tier: 'Professional', price: 'R$ 199/mês', features: ['Ilimitado', '5 usuários', 'Multi-moeda', 'API'], highlight: true },
                  { tier: 'Enterprise', price: 'R$ 499/mês', features: ['White label', 'API completa', 'SLA 99.9%', 'Suporte dedicado'] },
                ].map((plan) => (
                  <div
                    key={plan.tier}
                    className={cn(
                      'p-4 rounded-lg border',
                      plan.highlight
                        ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20'
                        : 'border-gray-200'
                    )}
                  >
                    {plan.highlight && (
                      <span className="text-xs font-medium text-teal-600 uppercase tracking-wide">
                        Popular
                      </span>
                    )}
                    <h4 className="font-semibold text-gray-900">{plan.tier}</h4>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{plan.price}</p>
                    <ul className="mt-4 space-y-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="text-sm text-gray-600 flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-teal-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* The Ask */}
            <div className="p-8 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 text-white">
              <div className="max-w-2xl mx-auto text-center">
                <h3 className="text-2xl font-bold">O Pedido</h3>
                <p className="mt-4 text-3xl font-bold text-teal-400">R$ 3 milhões em Seed</p>
                <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-white/10">
                    <p className="text-2xl font-bold">60%</p>
                    <p className="text-gray-300">Time de Produto</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/10">
                    <p className="text-2xl font-bold">20%</p>
                    <p className="text-gray-300">Go-to-Market</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/10">
                    <p className="text-2xl font-bold">20%</p>
                    <p className="text-gray-300">Operações</p>
                  </div>
                </div>
                <div className="mt-8 text-left">
                  <h4 className="font-semibold text-teal-400 mb-3">Próximos Passos</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                      Q2 2026: Beta fechado com 50 clientes
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                      Q4 2026: Lançamento público
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                      Q2 2027: 1.000 clientes pagantes
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
